import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { connect } from 'react-redux';
import config from 'config';
import qs from 'querystring';
import { parse as parseQueryString } from 'query-string';

import FeatureToggleUtils from 'FeatureToggleUtils';
import { LoginWidgetScreen, LoginWidget } from '@bizzabo/attendee-auth-api';
import { cookiesAccessCheck } from './ApiUtil';
import ThirdPartyCookieModal from './ThirdPartyCookieModal';
import {
    hideEmailLogin,
    showLoginV2Spinner,
    hideLoginV2Spinner,
} from '../../actions/LoginActions';
import {
    accountIdSelector,
    eventGroupIdSelector,
    websiteTypeSelector,
    eventIdSelector,
    whitelabelWidgetEnabledSelector,
    onlyEventDomainSelector,
    whitelabelPathSelector,
    whitelabelUrlSelector,
    getPublicWebsiteBaseUrlSelector,
    eventUniqueNameSelector,
    getRegistrationsUrl,
} from '../../selectors/EventSelectors';
import {
    isSSOLoginSelector,
    ssoAccountIdSelector,
    enableTicketIdLoginSelector,
    isLoginShownSelector,
    offsetTopSelector,
    getRedirectRingUrl,
    cookieConsentSrcSelector,
} from '../../selectors/LoginSelectors';
import { color1Selector } from '../../selectors/SiteConfSelectors';
import { notifyRedirectToTicketWidget } from './AgendaView/LoginPopover';
import { renderReactComponentWithRedux } from '../../hocs/withReduxProvider';
import { sendSnowplowEventByType } from '../../utils/SnowplowUtils';
// Import from the side-effect-free constants module, not the SnowplowTracker index,
// which injects sp.js at module-eval time (must stay behind consent — see SnowplowUtils).
import { DISABLE_SNOWPLOW_TRACKING } from '@bizzabo/widget-analytics/dist/SnowplowTracker/constants';

const AttendeeLogin = ({
    accountId,
    eventId,
    eventGroupId,
    onlyEventDomain,
    websiteType,
    whitelabelPath,
    whitelabelUrl,
    whitelabelWidgetEnabled,
    publicWebsiteBaseUrl,
    eventUniqueName,
    registrationsUrl,
    isSSOLogin,
    ssoAccountId,
    enableTicketIdLogin,
    authDropRedirectRing,
    isLoginShown,
    color1,
    inline = false,
    closable = true,
    showTitle = true,
    showChangeEmail = false,
    isShownByDefault = false,
    screen = LoginWidgetScreen.login,
    redirectUrl,
    onNotifyProfileChanged,
    onNotifyChangeEmail,
    hideRegisterAndLoginModal,
    hideEmailLogin,
    showLoginV2Spinner,
    hideLoginV2Spinner,
    offsetTopOnEmbedded,
    cookieConsentSrc,
    cookieConsentGroups: initialCookieConsentGroups
}) => {
    const isVisible = isLoginShown || isShownByDefault;

    const [isCookiesAccessible, setIsCookiesAccessible] = useState(true);
    const [cookieConsentGroups, setCookieConsentGroups] = useState(initialCookieConsentGroups);

    useEffect(() => {
        if (!isVisible) return;
        setCookieConsentGroups(initialCookieConsentGroups);
    }, [initialCookieConsentGroups, isVisible]);

    useEffect(() => {
        if (!isVisible || !cookieConsentSrc) return;
        const syncFromWindow = () => {
            if (typeof window.OnetrustActiveGroups === 'string') {
                setCookieConsentGroups(window.OnetrustActiveGroups);
            }
        };
        syncFromWindow();
        window.addEventListener('OneTrustGroupsUpdated', syncFromWindow);
        return () => window.removeEventListener('OneTrustGroupsUpdated', syncFromWindow);
    }, [cookieConsentSrc, isVisible]);

    const IBMBannedCountries = FeatureToggleUtils.isFeatureEnabled('IBMBannedCountries');
    const encryptedOriginUrl = `/webloginOrigin?eventGroupId=${eventGroupId}`;
    const emailLoginRedirectUrl = authDropRedirectRing
        || redirectUrl
        || document.location.href;
    const withSSO = screen === LoginWidgetScreen.login
        ? isSSOLogin
        : false;
    const isCommunityPage = redirectUrl
        ? /\/community/.test(redirectUrl)
        : false;
    const styles = {
        zIndex: inline
            ? 'auto'
            : isCommunityPage
                ? 1004
                : 1006
    };

    useEffect(() => {
        if (!isVisible) return;
        checkIsCookiesAccessible();
    }, [isVisible]);

    const checkIsCookiesAccessible = async() => {
        const response = await cookiesAccessCheck();
        const { cookiesAccessible } = response || {};
        if (!cookiesAccessible) {
            setIsCookiesAccessible(cookiesAccessible);
            hideRegisterAndLoginModal?.();
        } else {
            showLoginV2Spinner();
        }
    };

    const handleRedirectToRegistration = useCallback(() => {
        notifyRedirectToTicketWidget();

        const redirectUrl = registrationsUrl || publicWebsiteBaseUrl;
        window.top.location.href = redirectUrl;
    }, [eventId]);

    if (!isVisible) return null;

    const handleSetToken = (token) => {
        const q = qs.stringify({
            eventId,
            redirectUrl: authDropRedirectRing || window.location.href,
            t: token,
        });
        const url = `/auth/transfer/drop?${q}`;
        window.location.replace(url);
    };

    const isSnowplowEnabled = !FeatureToggleUtils.isFeatureEnabled(DISABLE_SNOWPLOW_TRACKING);

    const sendSnowplowEvent = (snowplowEvent) => {
        isSnowplowEnabled && sendSnowplowEventByType(snowplowEvent);
    };

    const handleProfileChange = profile => {
        onNotifyProfileChanged?.(profile);
    };

    const handleEmailChange = () => {
        onNotifyChangeEmail?.();
    };

    const handleIframeLoaded = () => {
        hideRegisterAndLoginModal?.();
        hideLoginV2Spinner();
    };

    const handleIframeLoadedWithError = () => {
        hideEmailLogin();
    };

    if (!isCookiesAccessible) {
        return (
            <ThirdPartyCookieModal
                show
                onHide={hideEmailLogin}
            />
        );
    }
    const isEmbedded = window.self !== window.parent;
    const { widget: isWidget } = parseQueryString(window.location.search);
    const defaultOffsetTopOnEmbedded = isEmbedded && isWidget ? 96 : null;
    const isCrossOriginIsolated = window.crossOriginIsolated;

    const node = (
        <LoginWidget
            inline={inline}
            closable={closable}
            screen={screen}
            colorMain={color1}
            style={styles}
            showTitle={showTitle}
            showChangeEmail={showChangeEmail}
            webloginUrl={config.webloginUrl}
            apiServer={config.apiServer}
            withSSO={withSSO}
            ssoAccountId={ssoAccountId}
            enableTicketIdLogin={enableTicketIdLogin}
            emailLoginRedirectUrl={emailLoginRedirectUrl}
            encryptedOriginUrl={encryptedOriginUrl}
            accountId={accountId}
            eventGroup={eventGroupId}
            eventId={eventId}
            modalOffsetTop={offsetTopOnEmbedded ?? defaultOffsetTopOnEmbedded}
            websiteType={websiteType}
            whitelabelPath={whitelabelPath}
            whitelabelUrl={whitelabelUrl}
            whitelabelWidgetEnabled={whitelabelWidgetEnabled}
            onlyEventDomain={onlyEventDomain}
            authDropRedirect={authDropRedirectRing}
            eventUniqueName={eventUniqueName}
            sendSnowplowEvent={sendSnowplowEvent}
            onNotifyClose={hideEmailLogin}
            onNotifyRedirectToRegistration={handleRedirectToRegistration}
            onSetToken={handleSetToken}
            onNotifyProfileChanged={handleProfileChange}
            onNotifyChangeEmail={handleEmailChange}
            onNotifyLoad={handleIframeLoaded}
            onNotifyLoadError={handleIframeLoadedWithError}
            IBMBannedCountries={IBMBannedCountries}
            cookieConsentSrc={cookieConsentSrc}
            cookieConsentGroups={cookieConsentGroups}
            isCrossOriginIsolated={isCrossOriginIsolated}
        />
    );

    if (inline) {
        return node;
    }

    return createPortal(node, document.body);
};

const AttendeeLoginWithRedux = connect(
    state => ({
        accountId: accountIdSelector(state),
        eventId: eventIdSelector(state),
        eventGroupId: eventGroupIdSelector(state),
        whitelabelWidgetEnabled: whitelabelWidgetEnabledSelector(state),
        onlyEventDomain: onlyEventDomainSelector(state),
        websiteType: websiteTypeSelector(state),
        whitelabelPath: whitelabelPathSelector(state),
        whitelabelUrl: whitelabelUrlSelector(state),
        publicWebsiteBaseUrl: getPublicWebsiteBaseUrlSelector(state),
        eventUniqueName: eventUniqueNameSelector(state),
        registrationsUrl: getRegistrationsUrl(state),
        isSSOLogin: isSSOLoginSelector(state),
        ssoAccountId: ssoAccountIdSelector(state),
        enableTicketIdLogin: enableTicketIdLoginSelector(state),
        isLoginShown: isLoginShownSelector(state),
        offsetTopOnEmbedded: offsetTopSelector(state),
        authDropRedirectRing: getRedirectRingUrl(state),
        color1: color1Selector(state),
        cookieConsentSrc: cookieConsentSrcSelector(state),
        cookieConsentGroups: !cookieConsentSrcSelector(state) ? undefined : (typeof window.OnetrustActiveGroups === 'string' ? window.OnetrustActiveGroups : 'C0001'),
    }),
    {
        hideEmailLogin,
        showLoginV2Spinner,
        hideLoginV2Spinner,
    }
)(AttendeeLogin);

export const renderAttendeeLogin = (props = null, root = null) => {
    renderReactComponentWithRedux(
        AttendeeLoginWithRedux,
        root ? root : document.getElementById('login-popup'),
        props
    );
};

export default AttendeeLoginWithRedux;
